import { useId } from 'react';
import { clsx } from 'clsx';

import type { ChangeEvent, ReactNode } from 'react';
import type { IconProps } from 'components/Icon';

import styles from './ControlsButton.module.css';

import { Icon } from 'components/Icon';

export interface ControlsButtonProps {
  /** to set type/behaviour of input radio or checkbox */
  type?: 'radio' | 'checkbox';
  /** button label */
  label: string | ReactNode;
  /** button value */
  value: string;
  /** set if icon is needed in font of label */
  iconName?: IconProps['name'];
  /** set if prefix is needed above of label */
  prefix?: string;
  /** set tooltip */
  tooltip?: ReactNode;
  /** set horizontal position of the tooltip */
  tooltipPosition?: 'left' | 'center' | 'right';
  /** set if used as radiogroup */
  name?: string;
  /** `true` to set squared button; otherwise, `false`. The default is `false`. Note: hides icon */
  forceSquared?: boolean;
  /** to set default status */
  checked?: boolean;
  /** `true` to disable the button; otherwise, `false`. The default is `false`. */
  disabled?: boolean;
  /** The callback function that is invoked when the radio button changes value. */
  onSetValue: (event: ChangeEvent<HTMLInputElement>) => void;
}

export const ControlsButton = ({
  type = 'checkbox',
  label,
  value,
  iconName,
  prefix,
  tooltip,
  tooltipPosition = 'center',
  name,
  forceSquared = false,
  checked,
  disabled,
  onSetValue,
}: ControlsButtonProps) => {
  const idPrefix = useId();
  const labelString = typeof label === 'string' ? label : value;
  const sanitizedLabel = labelString.replace(/\s+/g, '_');
  const baseId = `${idPrefix}${sanitizedLabel}`;
  const tooltipId = `${baseId}-tooltip`;

  return (
    <>
      <input
        type={type}
        id={baseId}
        name={name ?? sanitizedLabel}
        disabled={disabled}
        value={value}
        checked={checked}
        onChange={onSetValue}
        className={styles['controls-input']}
        aria-describedby={tooltip ? tooltipId : undefined}
      />
      <label
        htmlFor={baseId}
        className={clsx(styles['controls-button'], [
          forceSquared && styles['controls-button--squared'],
          prefix && styles['controls-button--with-prefix'],
          iconName && styles['controls-button--with-icon'],
          checked && styles['controls-button--checked'],
        ])}
      >
        {iconName && <Icon name={iconName} size={20} />}
        <div className={styles['controls-button__wrapper']}>
          {prefix && <div className={styles['controls-button__prefix']}>{prefix}</div>}
          {label}
        </div>
        {tooltip && (
          <div
            id={tooltipId}
            role="tooltip"
            className={clsx(styles['controls-button__tooltip'], styles[`controls-button__tooltip--${tooltipPosition}`])}
          >
            {tooltip}
          </div>
        )}
      </label>
    </>
  );
};
